import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Crosshair, Gift, LifeBuoy, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ranger/PageHeader";
import { Thread } from "@/pages/Tickets";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/pages/Servers";
import { api, errMsg, fmtTime } from "@/lib/api";

const inputCls = "border-[#1E2D4A] bg-[#050811]";

const useForm = (init) => { const [f, setF] = useState(init); return [f, (k) => (e) => setF({ ...f, [k]: e.target.value }), () => setF(init)]; };

const Tickets = ({ d, call }) => {
  const [f, on, reset] = useForm({ subject: "", text: "" });
  const [reply, setReply] = useState({});
  return (
    <Card title="Support tickets" icon={LifeBuoy} testId="portal-tickets">
      <form onSubmit={(e) => { e.preventDefault(); call("/tickets", f, "Ticket opened").then((ok) => ok && reset()); }} className="mb-4 space-y-2">
        <Input data-testid="portal-ticket-subject" required minLength={3} placeholder="Subject" value={f.subject} onChange={on("subject")} className={inputCls} />
        <Input data-testid="portal-ticket-text" required placeholder="Describe the issue" value={f.text} onChange={on("text")} className={inputCls} />
        <button data-testid="portal-ticket-submit" className={BTN_PRIMARY}>Open ticket</button>
      </form>
      {d.tickets.map((t) => (
        <div key={t.id} className="mb-3 border-t border-[#1E2D4A] pt-3">
          <div className="mb-2 text-sm font-semibold text-slate-100">#{t.number} {t.subject} <span className="font-mono text-xs uppercase text-slate-500">{t.status}</span></div>
          <Thread t={t} />
          {t.status === "open" && <form onSubmit={(e) => { e.preventDefault(); call(`/tickets/${t.id}/reply`, { text: reply[t.id] }, "Sent").then((ok) => ok && setReply({ ...reply, [t.id]: "" })); }} className="flex gap-2">
            <Input data-testid={`portal-ticket-reply-${t.id}`} required placeholder="Reply" value={reply[t.id] || ""} onChange={(e) => setReply({ ...reply, [t.id]: e.target.value })} className={inputCls} />
            <button className={BTN_SECONDARY}>Send</button>
          </form>}
        </div>
      ))}
    </Card>
  );
};

const Bounties = ({ d, call, me }) => {
  const [f, on, reset] = useForm({ gamertag: "", amount: "" });
  if (!d.tools.bounty_enabled) return null;
  return (
    <Card title="Bounties" icon={Crosshair} testId="portal-bounties">
      <form onSubmit={(e) => { e.preventDefault(); call("/bounties", { gamertag: f.gamertag, amount: Number(f.amount) }, "Bounty placed").then((ok) => ok && reset()); }} className="mb-4 flex gap-2">
        <Input data-testid="portal-bounty-target" required placeholder="Target gamertag" value={f.gamertag} onChange={on("gamertag")} className={inputCls} />
        <Input data-testid="portal-bounty-amount" required type="number" min={d.tools.bounty_min} placeholder={`Min ${d.tools.bounty_min}`} value={f.amount} onChange={on("amount")} className={`${inputCls} w-32 font-mono`} />
        <button data-testid="portal-bounty-submit" className={BTN_PRIMARY}>Place</button>
      </form>
      {d.bounties.length === 0 ? <p className="text-sm text-slate-500">No open bounties.</p> : <ul className="divide-y divide-[#1E2D4A]">{d.bounties.map((b) => (
        <li key={b.id} className="flex justify-between py-2 font-mono text-xs"><span className="text-slate-200">{b.target} · <span className="text-[#E5202E]">{b.amount}</span> · by {b.placer}</span>
          {b.placer_id === me && <button data-testid={`portal-bounty-cancel-${b.id}`} onClick={() => call(`/bounties/${b.id}/cancel`, {}, "Cancelled and refunded")} className="uppercase text-[#E5202E]">Cancel</button>}</li>
      ))}</ul>}
    </Card>
  );
};

const Clan = ({ d, call, me }) => {
  const [f, on, reset] = useForm({ name: "", tag: "", gamertag: "" });
  if (!d.tools.clans_enabled) return null;
  const c = d.clan;
  return (
    <Card title="Clan" icon={Shield} testId="portal-clan">
      {c ? (
        <div>
          <div className="mb-2 font-display text-2xl font-bold text-slate-100">[{c.tag}] {c.name}</div>
          <p className="mb-3 font-mono text-xs text-slate-400">{c.kills} kills · K/D {c.kd} · {c.members.length} members</p>
          <ul className="mb-3 space-y-1 text-sm">{c.members.map((m) => <li key={m.discord_id} className="flex justify-between text-slate-300">{m.gamertag || m.discord_id}{c.owner_id === me && m.discord_id !== me && <button onClick={() => call("/clan", { action: "kick", member_id: m.discord_id }, "Removed")} className="text-xs uppercase text-[#E5202E]">Kick</button>}</li>)}</ul>
          {c.owner_id === me && <form onSubmit={(e) => { e.preventDefault(); call("/clan", { action: "invite", gamertag: f.gamertag }, "Invite sent").then((ok) => ok && reset()); }} className="mb-3 flex gap-2">
            <Input data-testid="portal-clan-invite" required placeholder="Invite gamertag" value={f.gamertag} onChange={on("gamertag")} className={inputCls} /><button className={BTN_SECONDARY}>Invite</button></form>}
          {c.owner_id === me && c.invites.length > 0 && <p data-testid="portal-clan-pending" className="mb-3 text-xs text-slate-400">Pending invites: {c.invites.join(", ")}</p>}
          <button data-testid="portal-clan-leave" onClick={() => call("/clan", { action: "leave" }, "Done")} className={BTN_SECONDARY}>{c.owner_id === me ? "Disband" : "Leave"}</button>
        </div>
      ) : (
        <div className="space-y-3">
          {d.clan_invites.map((n) => <div key={n} className="flex items-center justify-between text-sm text-slate-300">Invite from <b>{n}</b><button data-testid={`portal-clan-join-${n}`} onClick={() => call("/clan", { action: "join", name: n }, `Joined ${n}`)} className={BTN_PRIMARY}>Join</button></div>)}
          <form onSubmit={(e) => { e.preventDefault(); call("/clan", { action: "create", name: f.name, tag: f.tag }, "Clan created").then((ok) => ok && reset()); }} className="flex gap-2">
            <Input data-testid="portal-clan-name" required minLength={3} placeholder="Clan name" value={f.name} onChange={on("name")} className={inputCls} />
            <Input data-testid="portal-clan-tag" required minLength={2} maxLength={5} placeholder="TAG" value={f.tag} onChange={on("tag")} className={`${inputCls} w-24 uppercase`} />
            <button data-testid="portal-clan-create" className={BTN_PRIMARY}>Create</button>
          </form>
        </div>
      )}
      {d.clans.length > 0 && <div className="mt-4 border-t border-[#1E2D4A] pt-3"><div className="rg-label mb-2">Top clans</div><ul className="space-y-1 font-mono text-xs">{d.clans.slice(0, 5).map((x, i) => <li key={x.id} className="text-slate-300">{i + 1}. [{x.tag}] {x.name} · {x.kills} kills</li>)}</ul></div>}
    </Card>
  );
};

const Giveaways = ({ d, call }) => d.giveaways.length === 0 ? null : (
  <Card title="Giveaways" icon={Gift} testId="portal-giveaways">
    <ul className="divide-y divide-[#1E2D4A]">{d.giveaways.map((g) => (
      <li key={g.id} className="flex items-center justify-between py-2 text-sm"><span className="text-slate-200">{g.prize}{g.coins ? ` + ${g.coins}` : ""} <span className="font-mono text-xs text-slate-500">· ends {fmtTime(g.ends_at)} · {g.entrants} entries</span></span>
        <button data-testid={`portal-gw-enter-${g.id}`} disabled={g.entered} onClick={() => call(`/giveaways/${g.id}/enter`, {}, "Entered!")} className={BTN_PRIMARY}>{g.entered ? "Entered" : "Enter"}</button></li>
    ))}</ul>
  </Card>
);

export const CommunityCards = ({ cid, me, reloadWallet }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/player/${cid}/community`).then((r) => setD(r.data)).catch(() => {}), [cid]);
  useEffect(() => { load(); }, [load]);
  const call = async (url, body, msg) => { try { await api.post(`/player/${cid}${url}`, body); toast.success(msg); load(); reloadWallet(); return true; } catch (e) { toast.error(errMsg(e)); return false; } };
  if (!d) return null;
  return (
    <>
      <Giveaways d={d} call={call} />
      <div className="grid gap-5 lg:grid-cols-2"><Clan d={d} call={call} me={me} /><Bounties d={d} call={call} me={me} /></div>
      <Tickets d={d} call={call} />
    </>
  );
};
